// Contract service for handling contract operations
import { API_BASE_URL } from '../config/api';
import { authFetch } from './session';

const API_URL = API_BASE_URL;
const JSON_HEADERS = { 'Content-Type': 'application/json' };

/** Antwort der API GET /lastschriftmandat/ (Backend LastschriftmandatResponse) */
export interface LastschriftmandatResponse {
  id?: string;
  file_name?: string;
  description?: string | null;
  mandate_reference?: string | null;
  payer_name?: string | null;
  payer_iban?: string | null;
  payer_bic?: string | null;
  payer_city?: string | null;
  created_at?: string;
  updated_at?: string;
}

const extractErrorMessage = async (response: Response): Promise<string> => {
  try {
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      const data = await response.clone().json().catch(() => null);
      if (data) {
        if (typeof data === 'string') return data;
        if (typeof (data as any).detail === 'string') return (data as any).detail;
        if (typeof (data as any).message === 'string') return (data as any).message;
      }
    }
    const text = await response.text().catch(() => '');
    return text;
  } catch (error) {
    console.error('Error extracting error message:', error);
    return '';
  }
};

const parseJsonResponse = async <T,>(response: Response): Promise<T> => {
  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    // If it's not JSON but successful, maybe return null or text? 
    // For now assuming JSON expected for these endpoints.
    // Some endpoints might return empty body on success?
    if (response.status === 204) return {} as T;
    // throw new Error(`Unexpected content type: ${contentType || 'unknown'}`);
  }
  return response.json();
};

const handleErrorResponse = async (response: Response): Promise<never> => {
  const detail = await extractErrorMessage(response);
  const statusSegment = `${response.status}${response.statusText ? ` ${response.statusText}` : ''}`;
  const message = detail ? `${statusSegment} - ${detail}` : `HTTP error! Status: ${response.status}`;
  throw new Error(message);
};

const arrayBufferToBase64 = (buffer: Uint8Array): string => {
  let binary = '';
  const len = buffer.byteLength;
  for (let i = 0; i < len; i += 1) binary += String.fromCharCode(buffer[i]);
  return btoa(binary);
};

const contractService = {
  // --- Main Contract ---

  createContract: async (contractData: { contract_text: string; signature_image: string; }) => {
    try {
      const response = await authFetch(`${API_URL}/contract/create`, {
        method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(contractData),
      });
      if (!response.ok) await handleErrorResponse(response);
      return await parseJsonResponse(response);
    } catch (error) { console.error(error); throw error; }
  },

  getContract: async () => {
    try {
      const response = await authFetch(`${API_URL}/contract/`, { method: 'GET', headers: JSON_HEADERS });
      if (!response.ok) { 
        if (response.status === 404) return null; 
        await handleErrorResponse(response); 
      }
      return await parseJsonResponse(response);
    } catch (error) { return null; }
  },

  getContractConfirmation: async () => {
    try {
      const response = await authFetch(`${API_URL}/contract/confirm`, { method: 'GET', headers: JSON_HEADERS });
      if (response.status === 404) return null;
      if (!response.ok) await handleErrorResponse(response);
      return parseJsonResponse(response);
    } catch (error) { return null; }
  },

  confirmMainContract: async (confirmed: boolean) => {
    try {
      const response = await authFetch(`${API_URL}/contract/confirm`, { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ confirmed }) });
      if (!response.ok) await handleErrorResponse(response);
      return parseJsonResponse(response);
    } catch (error) { throw error; }
  },

  updateMainContract: async (contractData: { contract_text: string; signature_image: string; }) => {
    try {
      const response = await authFetch(`${API_URL}/contract/update`, {
        method: 'PUT', headers: JSON_HEADERS, body: JSON.stringify(contractData),
      });
      if (!response.ok) await handleErrorResponse(response);
      return await parseJsonResponse(response);
    } catch (error) { console.error(error); throw error; }
  },

  // --- Data Processing Contract (AVV) ---

  createDataProcessingContract: async (contractData: any) => {
    try {
      const response = await authFetch(`${API_URL}/auftragsverarbeitung/create`, { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(contractData) });
      if (!response.ok) await handleErrorResponse(response);
      return await parseJsonResponse(response);
    } catch (error) { console.error(error); throw error; }
  },

  getDataProcessingContract: async () => {
    try {
      const response = await authFetch(`${API_URL}/auftragsverarbeitung/`, { method: 'GET', headers: JSON_HEADERS });
      if (!response.ok) { if (response.status === 404) return null; await handleErrorResponse(response); }
      return await parseJsonResponse(response);
    } catch (error) { return null; }
  },

  getDataProcessingConfirmation: async () => {
    try {
      const response = await authFetch(`${API_URL}/auftragsverarbeitung/confirm`, { method: 'GET', headers: JSON_HEADERS });
      if (response.status === 404) return null;
      if (!response.ok) await handleErrorResponse(response);
      return parseJsonResponse(response);
    } catch (error) { return null; }
  },

  confirmDataProcessingContract: async (confirmed: boolean) => {
    try {
      const response = await authFetch(`${API_URL}/auftragsverarbeitung/confirm`, { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ confirmed }) });
      if (!response.ok) await handleErrorResponse(response);
      return parseJsonResponse(response);
    } catch (error) { throw error; }
  },

  updateAuftragsverarbeitung: async (contractData: { contract_text: string; signature_image: string; }) => {
    try {
      const response = await authFetch(`${API_URL}/auftragsverarbeitung/update`, {
        method: 'PUT', headers: JSON_HEADERS, body: JSON.stringify(contractData),
      });
      if (!response.ok) await handleErrorResponse(response);
      return await parseJsonResponse(response);
    } catch (error) { console.error(error); throw error; }
  },

  // --- Lastschriftmandat ---

  getLastschriftmandat: async (): Promise<LastschriftmandatResponse | null> => {
    try {
      const response = await authFetch(`${API_URL}/lastschriftmandat/`, { method: 'GET', headers: JSON_HEADERS });
      if (!response.ok) { if (response.status === 404) return null; await handleErrorResponse(response); }
      return await parseJsonResponse<LastschriftmandatResponse>(response);
    } catch (error) { return null; }
  },

  uploadLastschriftmandat: async (data: any) => {
    try {
      const response = await authFetch(`${API_URL}/lastschriftmandat/upload`, { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(data) });
      if (!response.ok) await handleErrorResponse(response);
      return await response.json();
    } catch (error) { throw error; }
  },

  updateLastschriftmandat: async (data: any) => {
    try {
      const response = await authFetch(`${API_URL}/lastschriftmandat/update`, { method: 'PUT', headers: JSON_HEADERS, body: JSON.stringify(data) });
      if (!response.ok) await handleErrorResponse(response);
      return await response.json();
    } catch (error) { throw error; }
  },

  deleteLastschriftmandat: async () => {
    const response = await authFetch(`${API_URL}/lastschriftmandat/delete`, { method: 'DELETE' });
    if (!response.ok) await handleErrorResponse(response);
  },

  downloadLastschriftmandat: async () => {
    try {
      const response = await authFetch(`${API_URL}/lastschriftmandat/download`, { method: 'GET', headers: JSON_HEADERS });
      if (!response.ok) await handleErrorResponse(response);
      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('application/json')) return await response.json();
      const arrayBuffer = await response.arrayBuffer();
      const pdfBase64 = arrayBufferToBase64(new Uint8Array(arrayBuffer));
      let fileName = 'SEPA-Lastschriftmandat.pdf';
      const contentDisposition = response.headers.get('content-disposition') || '';
      const fileNameMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/);
      if (fileNameMatch) fileName = decodeURIComponent(fileNameMatch[1] || fileNameMatch[2]);
      return { pdf_file: `data:${contentType};base64,${pdfBase64}`, file_name: fileName };
    } catch (error) { throw error; }
  }
};

export default contractService;